/**
 * Platform Admin Layout
 */

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePlatformAuth } from '../contexts/PlatformAuthContext';
import { LayoutDashboard, Building2, LogOut, Shield, FileText, Settings } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { admin, logout } = usePlatformAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
    { path: '/organizations', icon: <Building2 size={20} />, label: 'Organizations' },
    { path: '/logs', icon: <FileText size={20} />, label: 'Logs' },
    { path: '/settings', icon: <Settings size={20} />, label: 'Settings' },
  ];

  return (
    <div style={styles.layout}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.logo}>
          <Shield size={24} color="#6366f1" />
          <span style={styles.logoText}>Platform Admin</span>
        </div>

        <nav style={styles.nav}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                ...styles.navItem,
                backgroundColor: location.pathname === item.path ? '#334155' : 'transparent',
                color: location.pathname === item.path ? '#f8fafc' : '#94a3b8',
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.adminInfo}>
            <div style={styles.adminAvatar}>
              {(admin?.email || 'A').charAt(0).toUpperCase()}
            </div>
            <div style={styles.adminDetails}>
              <span style={styles.adminName}>{admin?.displayName || admin?.email || 'Admin'}</span>
              <span style={styles.adminRole}>{admin?.role || 'admin'}</span>
            </div>
          </div>
          <button onClick={handleLogout} style={styles.logoutButton}>
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#0f172a',
  },
  sidebar: {
    width: '250px',
    backgroundColor: '#1e293b',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid #334155',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1.5rem',
    borderBottom: '1px solid #334155',
  },
  logoText: {
    color: '#f8fafc',
    fontSize: '1rem',
    fontWeight: 600,
  },
  nav: {
    flex: 1,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    textDecoration: 'none',
    transition: 'all 0.2s',
    fontSize: '0.875rem',
  },
  sidebarFooter: {
    padding: '1rem',
    borderTop: '1px solid #334155',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  adminInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  adminAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#6366f1',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  adminDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  adminName: {
    color: '#f8fafc',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  adminRole: {
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'capitalize',
  },
  logoutButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  main: {
    flex: 1,
    overflow: 'auto',
  },
};
